import {
  getFirestore,
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';

export class InvoiceService {
  constructor(user) {
    this.user = user;
    this.firestore = getFirestore();
  }

  async createInvoice(invoiceData) {
    try {
      console.log("Creating invoice for user:", this.user.uid);
      
      // Check if user has Stripe account ID
      const userRef = doc(this.firestore, 'users', this.user.uid);
      const userDoc = await getDoc(userRef);
      
      // Get the Stripe account ID from either the users or profiles collection
      let stripeAccountId = null;
      if (userDoc.exists() && userDoc.data().stripeAccountId) {
        stripeAccountId = userDoc.data().stripeAccountId;
        console.log("Found stripeAccountId in users collection:", stripeAccountId);
      } else {
        const profileRef = doc(this.firestore, 'profiles', this.user.uid);
        const profileDoc = await getDoc(profileRef);
        if (profileDoc.exists() && profileDoc.data().stripeConnectedAccountId) {
          stripeAccountId = profileDoc.data().stripeConnectedAccountId;
          console.log("Found stripeConnectedAccountId in profiles collection:", stripeAccountId);
        } else {
          console.log("No Stripe account ID found for user:", this.user.uid);
          console.log("User document exists:", userDoc.exists());
          if (userDoc.exists()) {
            console.log("User document data:", userDoc.data());
          }
        }
      }
      
      // 1) Create the invoice in the top-level "invoices" collection
      //    so the payment flow can find it by invoiceId:
      const invoicesRef = collection(this.firestore, 'invoices');
      const invoiceToCreate = {
        ...invoiceData,
        userId: this.user.uid,
        status: 'PENDING',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        connectedAccountId: stripeAccountId, // Add Stripe account ID to invoice
        // Initialize sentDate as null - will be set when invoice is first sent
        sentDate: null
      };
      
      console.log("Saving invoice with data:", { ...invoiceToCreate, createdAt: "serverTimestamp", updatedAt: "serverTimestamp" });
      
      const docRef = await addDoc(invoicesRef, invoiceToCreate);

      const invoiceId = docRef.id; // We'll return this for routing

      // 2) Update user's invoice stats
      if (userDoc.exists()) {
        const userData = userDoc.data();
        await updateDoc(userRef, {
          'stats.invoices': (userData.stats?.invoices || 0) + 1,
          'stats.pendingAmount':
            (userData.stats?.pendingAmount || 0) + parseFloat(invoiceData.amount)
        });
      }

      // 3) If a matching client exists, update that client's stats
      if (invoiceData.email) {
        const clientsRef = collection(this.firestore, 'users', this.user.uid, 'clients');
        const q = query(clientsRef, where('email', '==', invoiceData.email));
        const clientSnapshot = await getDocs(q);

        if (!clientSnapshot.empty) {
          const clientDoc = clientSnapshot.docs[0];
          await updateDoc(clientDoc.ref, {
            'stats.invoices': (clientDoc.data().stats?.invoices || 0) + 1,
            lastInvoiceDate: new Date()
          });
        }
      }

      // Return the newly created invoice's ID so you can navigate to /preview-invoice/:invoiceId
      return invoiceId;
    } catch (error) {
      console.error('Error in createInvoice:', error);
      throw error;
    }
  }

  async updateInvoice(invoiceId, updatedData, previousData = null) {
    try {
      console.log("Updating invoice:", invoiceId);
      
      const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
      
      // Remove fields that shouldn't be updated
      const { id, userId, createdAt, ...dataToUpdate } = updatedData;
      
      // Add updated timestamp and edit tracking
      dataToUpdate.updatedAt = serverTimestamp();
      dataToUpdate.lastEditedBy = this.user.uid;
      
      // Create audit trail entry if previous data provided
      if (previousData) {
        const auditEntry = {
          timestamp: new Date(),
          editedBy: this.user.uid,
          changes: this.getChangedFields(previousData, updatedData),
          action: 'invoice_edited'
        };
        
        // Add to edit history
        const currentHistory = previousData.editHistory || [];
        dataToUpdate.editHistory = [...currentHistory, auditEntry];
      }
      
      await updateDoc(invoiceRef, dataToUpdate);
      
      console.log("✅ Invoice updated successfully");
      return invoiceId;
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  }

  // Helper method to identify changed fields
  getChangedFields(oldData, newData) {
    const changes = {};
    const fieldsToTrack = ['clientName', 'email', 'project', 'amount', 'dueDate', 'lineItems', 'taxRate', 'notes'];
    
    fieldsToTrack.forEach(field => {
      const oldValue = oldData[field];
      const newValue = newData[field];
      
      // Compare values (handle dates and objects specially)
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes[field] = {
          from: oldValue,
          to: newValue
        };
      }
    });
    
    return changes;
  }

  async getInvoice(invoiceId) {
    try {
      const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
      const invoiceDoc = await getDoc(invoiceRef);
      
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found');
      }
      
      return {
        id: invoiceDoc.id,
        ...invoiceDoc.data()
      };
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }

  static async markAsPaidManually(invoiceId, paymentMethod = 'Manual Payment') {
    try {
      const firestore = getFirestore();
      const invoiceRef = doc(firestore, 'invoices', invoiceId);
      
      // First, get the invoice data to check email settings
      const invoiceSnap = await getDoc(invoiceRef);
      if (!invoiceSnap.exists()) {
        throw new Error('Invoice not found');
      }
      
      const invoiceData = invoiceSnap.data();
      
      // Update invoice status to paid
      await updateDoc(invoiceRef, {
        status: 'paid',
        paidAt: new Date(),
        updatedAt: new Date(),
        paymentMethod: paymentMethod,
        manuallyMarkedAsPaid: true
      });
      
      // Check if payment confirmation emails should be sent
      const emailSettings = invoiceData.paymentEmailSettings || {};
      const shouldSendEmail = emailSettings.sendConfirmationEmail && !invoiceData.paymentEmailsSent;
      
      if (shouldSendEmail) {
        console.log('🔄 Manual payment marked - sending confirmation emails');
        
        try {
          // Import EmailService dynamically to avoid circular imports
          const { default: EmailService } = await import('./EmailService');
          
          // Trigger the same email confirmation process as automatic payments
          await EmailService.triggerPaymentConfirmationEmails(invoiceId);
          
          // Mark emails as sent
          await updateDoc(invoiceRef, {
            paymentEmailsSent: true,
            paymentEmailsSentAt: new Date()
          });
          
          console.log('✅ Payment confirmation emails sent for manual payment');
        } catch (emailError) {
          console.error('❌ Failed to send payment confirmation emails:', emailError);
          // Don't fail the entire operation if email fails
        }
      } else {
        console.log('⏭️ Skipping email for manual payment:', {
          emailsEnabled: emailSettings.sendConfirmationEmail,
          alreadySent: invoiceData.paymentEmailsSent
        });
      }
      
      return { success: true, message: 'Invoice marked as paid successfully' };
    } catch (error) {
      console.error('Error marking invoice as paid manually:', error);
      throw new Error('Failed to mark invoice as paid');
    }
  }
}